import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Play, Pause, Loader, Save, Download, Trash2, AlertCircle, 
  CheckCircle, Volume2, RefreshCw, Plus, Edit, Music, Mic, MicOff, StopCircle,
  ArrowLeft, Settings, Minus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import logger from '../utils/logger';
import GuideModal from './GuideModal';
import './DesktopComponentStyles.css';

const AudioVisualization = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState({
    mission: '',
    vision: '',
    oneWordFocus: '',
    keysToKeepItSimple: [],
    affirmations: [],
    selfTalk: [],
    beliefs: [],
    identityStatement: {
      iHave: [],
      iAm: [],
      iLove: [],
      iGive: []
    }
  });
  const [inputText, setInputText] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingFromLibrary, setIsPlayingFromLibrary] = useState(false);
  const [savedAudios, setSavedAudios] = useState([]);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [customSettings, setCustomSettings] = useState({
    voice: 'calm',      // calm, energetic, soothing
    background: 'none', // none, ambient, nature
    pace: 'medium',     // slow, medium, fast
    duration: 5         // in minutes
  });
  const [isGeneratingPersonalizedScript, setIsGeneratingPersonalizedScript] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [speechSynthesisActive, setSpeechSynthesisActive] = useState(false);
  const [speechSynthesisReady, setSpeechSynthesisReady] = useState(false);
  const speechSynthesisRef = useRef(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingPermission, setRecordingPermission] = useState(false);
  
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  useEffect(() => {
    fetchUserData();
    fetchSavedAudios();
    
    // PHASE 2 AUTOMATIC AUDIO GENERATION - TEMPORARILY COMMENTED OUT
    // This function automatically processes onboarding Mindset Audio when component loads
    // Users will manually generate audio files when they want them
    // checkOnboardingAudio();
    
    // Cleanup function to cancel any active speech synthesis when component unmounts
    return () => {
      // Cancel any active speech synthesis
      if (window.speechSynthesis && speechSynthesisActive) {
        logger.debug('Canceling active speech synthesis on component unmount');
        window.speechSynthesis.cancel();
        setSpeechSynthesisActive(false);
      }
      
      // Also stop any audio playback
      if (audioRef.current) {
        logger.debug('Stopping audio playback on component unmount');
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
  }, [speechSynthesisActive]);

  useEffect(() => {
    // PHASE 2 AUTOMATIC AUDIO GENERATION - TEMPORARILY COMMENTED OUT
    // Users will manually generate audio files when they want them
    // processPendingAudioGeneration();
  }, []);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      logger.debug('Fetching user data for audio visualization');
      
      // Ensure the API URL is properly formatted
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      
      // Fetch MVP data with the correct /api prefix
      const mvpResponse = await axios.get(`${baseUrl}/api/users/mvp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch mindset data
      const mindsetResponse = await axios.get(`${baseUrl}/api/mindset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      logger.debug('User data fetched successfully');
      
      // Combine the data
      setUserData({
        mission: mvpResponse.data.mission || '',
        vision: mvpResponse.data.vision || '',
        oneWordFocus: mindsetResponse.data.oneWordFocus || '',
        keysToKeepItSimple: mindsetResponse.data.keysToKeepItSimple || [],
        affirmations: mindsetResponse.data.affirmations || [],
        selfTalk: mindsetResponse.data.selfTalk || [],
        beliefs: mindsetResponse.data.beliefs || [],
        identityStatement: mindsetResponse.data.identityStatement || {
          iHave: [],
          iAm: [],
          iLove: [],
          iGive: []
        }
      });
      
      setLoading(false);
    } catch (error) {
      logger.error('Error fetching user data for audio visualization', error);
      setError('Failed to load your data. Please try again.');
      setLoading(false);
    }
  };

  const fetchSavedAudios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      logger.debug('Fetching saved Mindset Audio visualizations');
      
      // Ensure the API URL is properly formatted
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      logger.debug(`Using base URL for API calls: ${baseUrl}`);
      
      // Log the exact endpoint being called
      const endpoint = `${baseUrl}/api/ai/audio`;
      logger.debug(`Calling endpoint to fetch Mindset Audio: ${endpoint}`);
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        logger.debug(`Fetched ${response.data.length} Mindset Audio records from server`);
        
        // FILTER: Only show Mindset Audio records, exclude Mental Imagery
        const mindsetAudios = response.data.filter(audio => {
          const isMindsetAudio = audio.type === 'mindset';
          if (!isMindsetAudio) {
            logger.debug(`Filtering out non-mindset record: ${audio._id} (type: ${audio.type}, title: ${audio.title})`);
          }
          return isMindsetAudio;
        });
        
        logger.debug(`After type filtering: ${mindsetAudios.length} Mindset Audio records (filtered out ${response.data.length - mindsetAudios.length} non-mindset records)`);
        
        // Log the raw data for debugging - ENHANCED FOR DUPLICATE INVESTIGATION
        logger.debug('Raw Mindset Audio records:', mindsetAudios.map(audio => ({
          id: audio._id,
          title: audio.title,
          hasScript: !!audio.script,
          scriptLength: audio.script ? audio.script.length : 0,
          scriptPreview: audio.script ? audio.script.substring(0, 100) + '...' : 'No script',
          hasAudioUrl: !!audio.audioUrl,
          audioUrl: audio.audioUrl,
          createdAt: audio.createdAt,
          type: audio.type,
          // Add source identification
          isOnboardingScript: audio.title?.includes('Onboarding') || audio.title?.includes('onboarding'),
          isPersonalizedScript: audio.title?.includes('Personalized') || audio.title?.includes('My Mindset'),
          source: audio.source || 'unknown'
        })));
        
        // DETAILED ANALYSIS: Log each script's first 200 characters for comparison
        logger.info('=== DUPLICATE INVESTIGATION: Script Content Analysis ===');
        mindsetAudios.forEach((audio, index) => {
          logger.info(`Script ${index + 1} (ID: ${audio._id}):`, {
            title: audio.title,
            createdAt: audio.createdAt,
            type: audio.type,
            scriptStart: audio.script ? audio.script.substring(0, 200) + '...' : 'No script',
            audioUrl: audio.audioUrl,
            isScriptOnly: audio.audioUrl === 'pending' || !audio.audioUrl
          });
        });
        
        // Process the data to ensure proper state flags
        const processedAudios = mindsetAudios.map(audio => {
          // Improved validation for audio URLs
          const hasValidUrl = audio.audioUrl && 
                            audio.audioUrl !== 'pending' && 
                            !audio.audioUrl.includes('pending') &&
                            audio.audioUrl.length > 10;
          
          // Check for database audio URLs specifically (they contain /api/ai/audio/serve/)
          const isDatabaseAudio = hasValidUrl && 
                                (audio.audioUrl.includes('/api/ai/audio/serve/') ||
                                 audio.audioData); // Also check for audioData presence
        
          // Either filesystem or database audio is valid
          const isValidAudio = hasValidUrl || 
                            isDatabaseAudio || 
                            (audio.audioData && audio.audioData.length > 1000); // Check for substantial audioData
        
          logger.debug(`Audio record ${audio._id} validation:`, {
            title: audio.title || 'Untitled',
            hasValidUrl,
            isDatabaseAudio,
            isValidAudio,
            audioUrl: audio.audioUrl,
            hasAudioData: !!audio.audioData,
            isScriptOnly: audio.isScriptOnly
          });
        
          // Ensure isScriptOnly flag is correct based on audioUrl and audioData
          return {
            ...audio,
            isScriptOnly: !isValidAudio
          };
        });
        
        // IMPROVED DEDUPLICATION LOGIC
        // Use a Map to track unique records by both ID and content
        const uniqueMap = new Map();
        
        // First, identify and keep the most recent version of each record
        // Group by script content first (most reliable deduplication method)
        processedAudios.forEach(audio => {
          const script = audio.script?.toLowerCase().trim();
          
          // Skip records with empty script only if they don't have valid audio
          if (!script && !audio.audioUrl) {
            logger.debug(`Skipping record with no script and no audio: ${audio._id}`);
            return;
          }
          
          // Use script content as primary key for deduplication
          const key = script || audio._id; // Use ID as fallback if no script
          
          if (uniqueMap.has(key)) {
            const existing = uniqueMap.get(key);
            
            // If the current record has audio and the existing one doesn't,
            // or if the current record is newer, replace the existing one
            const currentHasAudio = !audio.isScriptOnly;
            const existingHasAudio = !existing.isScriptOnly;
            
            logger.debug(`Duplicate found for key "${key.substring(0, 20)}...": 
              - Existing: ${existing._id} (hasAudio: ${existingHasAudio}, date: ${existing.createdAt})
              - Current: ${audio._id} (hasAudio: ${currentHasAudio}, date: ${audio.createdAt})
              - Decision: ${(currentHasAudio && !existingHasAudio) || 
                (currentHasAudio === existingHasAudio && 
                new Date(audio.createdAt) > new Date(existing.createdAt)) ? 'keep current' : 'keep existing'}`);
            
            if ((currentHasAudio && !existingHasAudio) || 
                (currentHasAudio === existingHasAudio && 
                 new Date(audio.createdAt) > new Date(existing.createdAt))) {
              uniqueMap.set(key, audio);
            }
          } else {
            logger.debug(`Adding new unique record: ${audio._id} (${audio.title || 'Untitled'}, hasAudio: ${!audio.isScriptOnly})`);
            uniqueMap.set(key, audio);
          }
        });
        
        // Convert the map values to an array
        const dedupedAudios = Array.from(uniqueMap.values());
        
        // Sort by creation date (newest first)
        dedupedAudios.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        logger.info(`Filtered ${response.data.length} records down to ${dedupedAudios.length} unique Mindset Audio records`);
        
        if (dedupedAudios.length === 0) {
          logger.warn('No valid Mindset Audio Visualization found in saved audios.');
        } else {
          logger.debug(`Found ${dedupedAudios.length} valid audio records`);
        }
        
        setSavedAudios(dedupedAudios);
        logger.debug('Audio records processed, deduplicated, and sorted');
      } else {
        logger.warn('Unexpected response format from audio API', response.data);
        setSavedAudios([]);
      }
    } catch (error) {
      logger.error('Error fetching saved audios:', error);
      setError('Failed to load your saved audio visualizations');
      setSavedAudios([]);
    } finally {
      setLoading(false);
    }
  };

  const userDataTextContent = (htmlContent) => {
    // Simple function to extract text from HTML content
    if (!htmlContent) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const handleTemplateSelect = (templateId) => {
    const template = savedTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setInputText(template.text);
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    // If user modifies the text, deselect the template
    if (selectedTemplate && e.target.value !== selectedTemplate.text) {
      setSelectedTemplate(null);
    }
  };

  const handleSettingChange = (setting, value) => {
    setCustomSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const generatePersonalizedScript = async () => {
    setIsGeneratingPersonalizedScript(true);
    setError('');
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      logger.debug('Checking for existing Mindset Audio scripts before generating new one');
      
      try {
        const existingResponse = await axios.get(`${API_URL}/api/ai/audio`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (existingResponse.data && Array.isArray(existingResponse.data)) {
          const existingMindsetAudios = existingResponse.data.filter(audio => 
            audio.type === 'mindset' && audio.script && audio.script.trim().length > 0
          );
          
          if (existingMindsetAudios.length > 0) {
            logger.info(`Found ${existingMindsetAudios.length} existing Mindset Audio script(s). Preventing duplicate creation.`);
            setError('You already have a Mindset Audio script. Please use the existing script or delete it first before creating a new one.');
            return;
          }
        }
      } catch (checkError) {
        logger.warn('Error checking for existing scripts, proceeding with generation:', checkError);
        // Continue with generation if we can't check for existing scripts
      }
      
      logger.debug('No existing Mindset Audio scripts found, proceeding with generation');
      
      // Prepare user data for script generation
      const userDataForScript = {
        settings: customSettings,
        userData: {
          name: userData.name || '',
          sport: userData.sport || '',
          sportPosition: userData.sportPosition || '',
          mission: userData.mission || '',
          vision: userData.vision || '',
          oneWordFocus: userData.oneWordFocus || '',
          keysToKeepItSimple: userData.keysToKeepItSimple || [],
          affirmations: userData.affirmations || [],
          selfTalk: userData.selfTalk || [],
          beliefs: userData.beliefs || [],
          identityStatement: userData.identityStatement || {
            iHave: [],
            iAm: [],
            iLove: [],
            iGive: []
          }
        }
      };
      
      let scriptGenerated = false;
      
      // Try primary script generation endpoint
      try {
        logger.debug('Attempting primary script generation endpoint');
        const response = await axios.post(
          `${API_URL}/api/ai/generate-script`,
          userDataForScript,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.script) {
          setUserData(prevData => ({
            ...prevData,
            mindsetScript: response.data.script
          }));
          
          // Set a default title if none exists
          if (!inputText.trim()) {
            setInputText('My Mindset Audio - ' + new Date().toLocaleDateString());
          }
          
          setMessage('Script generated successfully! You can now generate audio from this script.');
          
          // No longer automatically generate audio - user must click the button
          logger.debug('Script generated successfully from primary endpoint');
          scriptGenerated = true;
        } else {
          throw new Error('Invalid response format from primary endpoint');
        }
      } catch (primaryError) {
        logger.warn('Primary script generation endpoint failed, trying fallback', primaryError);
        
        // Try fallback script endpoint
        try {
          logger.debug('Attempting fallback script generation endpoint');
          const fallbackResponse = await axios.post(
            `${API_URL}/api/ai/mindset-script`,
            {
              settings: customSettings,
              userData: {
                name: userData.name || '',
                sport: userData.sport || '',
                sportPosition: userData.sportPosition || '',
                mindset: {
                  oneWordFocus: userData.oneWordFocus || '',
                  keysToKeepItSimple: userData.keysToKeepItSimple || [],
                  affirmations: userData.affirmations || [],
                  selfTalk: userData.selfTalk || [],
                  beliefs: userData.beliefs || [],
                  identityStatement: userData.identityStatement || {}
                }
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (fallbackResponse.data && fallbackResponse.data.script) {
            // Set the script to the mindsetScript field in userData instead of inputText (title)
            setUserData(prevData => ({
              ...prevData,
              mindsetScript: fallbackResponse.data.script
            }));
            
            // Set a default title if none exists
            if (!inputText.trim()) {
              setInputText('My Mindset Audio - ' + new Date().toLocaleDateString());
            }
            
            setMessage('Script generated successfully! You can now generate audio from this script.');
            
            // No longer automatically generate audio - user must click the button
            logger.debug('Script generated successfully from fallback endpoint');
            scriptGenerated = true;
          } else {
            throw new Error('Invalid response format from fallback endpoint');
          }
        } catch (fallbackError) {
          logger.error('All script generation endpoints failed', fallbackError);
          throw new Error('Failed to generate script. Please try again later.');
        }
      }
      
      if (!scriptGenerated) {
        throw new Error('Failed to generate script. Please try again later.');
      }
    } catch (error) {
      logger.error('Error generating personalized script:', error);
      setError(error.message || 'Failed to generate script. Please try again later.');
    } finally {
      setIsGeneratingPersonalizedScript(false);
    }
  };

  const generateAudio = async () => {
    setGenerating(true);
    setError('');
    
    try {
      logger.debug('Generating audio from script');
      
      // Check if we have a script in the textarea
      if (!userData.mindsetScript || !userData.mindsetScript.trim()) {
        setError('Please enter or generate a script first.');
        setGenerating(false);
        return;
      }
      
      // Check if we have a title
      if (!inputText.trim()) {
        setInputText('My Mindset Audio - ' + new Date().toLocaleDateString());
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Try the primary endpoint first
      try {
        logger.debug('Attempting primary text-to-speech endpoint');
        const response = await axios.post(
          `${API_URL}/api/ai/generate-audio`,
          {
            text: userData.mindsetScript,
            title: inputText,
            settings: customSettings
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        logger.info('Audio generated successfully from primary endpoint');
        
        // Ensure we have a full URL for the audio
        let audioUrlToUse = response.data.audioUrl;
        if (audioUrlToUse && audioUrlToUse.startsWith('/')) {
          audioUrlToUse = `${API_URL}${audioUrlToUse}`;
        }
        
        setAudioUrl(audioUrlToUse);
        setIsPlaying(false);
        
        // Automatically save both script and audio to database
        try {
          const saveData = {
            audioUrl: audioUrlToUse,
            title: inputText || 'My Mindset Audio',
            text: userData.mindsetScript,
            settings: customSettings
          };
          
          const saveResponse = await axios.post(
            `${API_URL}/api/ai/save-audio`,
            saveData,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          logger.info('Audio and script saved to library successfully');
          
          setMessage('Audio generated and saved to library successfully!');
          setTimeout(() => setMessage(''), 3000);
          
          // Refresh the saved audios list
          fetchSavedAudios();
          
        } catch (saveError) {
          logger.error('Error saving generated audio:', saveError);
          // Audio was generated but save failed - still show success for audio generation
          setMessage('Audio generated successfully! Please click "Save to Library" to save it.');
        }
        
      } catch (error) {
        logger.warn('Primary endpoint failed, falling back to browser speech synthesis', error);
        
        // Fallback to browser's built-in speech synthesis as last resort
        logger.debug('Attempting to use browser speech synthesis as last resort');
        try {
          if ('speechSynthesis' in window) {
            // Stop any existing speech synthesis
            if (speechSynthesisActive) {
              window.speechSynthesis.cancel();
            }
            
            // Create a new utterance
            const utterance = new SpeechSynthesisUtterance(userData.mindsetScript);
            
            // Set voice options if available
            if (window.speechSynthesis.getVoices().length > 0) {
              // Try to find a calm, clear voice
              const voices = window.speechSynthesis.getVoices();
              const preferredVoice = voices.find(voice => 
                voice.name.toLowerCase().includes('female') || 
                voice.name.toLowerCase().includes('samantha') ||
                voice.name.toLowerCase().includes('google')
              ) || voices[0];
              
              utterance.voice = preferredVoice;
            }
            
            // Set rate based on settings
            if (customSettings.pace === 'slow') {
              utterance.rate = 0.8;
            } else if (customSettings.pace === 'fast') {
              utterance.rate = 1.2;
            } else {
              utterance.rate = 1.0; // medium
            }
            
            // Set up event handlers
            utterance.onstart = () => {
              logger.debug('Speech synthesis started');
              setSpeechSynthesisActive(true);
              setMessage('Using browser speech synthesis. Audio quality may be limited.');
            };
            
            utterance.onend = () => {
              logger.debug('Speech synthesis ended');
              setSpeechSynthesisActive(false);
              setMessage('Speech synthesis complete. Click "Save to Library" to save this audio.');
            };
            
            utterance.onerror = (event) => {
              logger.error('Speech synthesis error:', event);
              setSpeechSynthesisActive(false);
              setError('Speech synthesis failed. Please try again or use the recording feature.');
            };
            
            // Store reference to control playback
            speechSynthesisRef.current = utterance;
            
            // IMPORTANT: Don't start playback automatically
            // Instead, set a flag and show a button to start playback
            setSpeechSynthesisReady(true);
            setAudioUrl('speech-synthesis-ready');
            setIsPlaying(false);
            
            // Create a simple audio control UI
            setMessage(
              'Browser speech synthesis ready. ' +
              'Click "Play Speech" below to start audio. ' +
              'You can stop playback at any time.'
            );
            
            return; // Exit early, don't throw an error
          } else {
            throw new Error('Speech synthesis not supported in this browser');
          }
        } catch (speechError) {
          logger.error('Browser speech synthesis fallback failed', speechError);
          throw new Error('All audio generation methods failed');
        }
      }
    } catch (error) {
      logger.error('Error generating audio', error);
      
      // Provide more specific error messages based on error type
      if (error.code === 'ECONNABORTED') {
        setError('Audio generation timed out. The server might be busy. Please try again later.');
      } else if (error.response && error.response.status === 404) {
        setError('Audio generation service is currently unavailable. Please try the "Record Yourself" option instead.');
        setMessage('Tip: You can record yourself reading the script aloud using the recording feature below.');
      } else {
        setError('Failed to generate audio. Please try again or use the recording feature as an alternative.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const generateAudioForScript = async (id) => {
    setGenerating(true);
    setMessage('Generating audio...');
    
    try {
      logger.debug(`PHASE 2: Generating audio for Mindset Audio script ID: ${id}`);
      
      const token = localStorage.getItem('token');
      if (!token) {
        logger.error('No authentication token found for audio generation');
        throw new Error('No authentication token found');
      }
      
      // Enhanced logging for localStorage state
      logger.debug('PHASE 2: Current localStorage state:', {
        onboardingMindsetAudioId: localStorage.getItem('onboardingMindsetAudioId'),
        onboardingMindsetNeedsAudio: localStorage.getItem('onboardingMindsetNeedsAudio'),
        onboardingMindsetScript: localStorage.getItem('onboardingMindsetScript') ? 'present' : 'missing',
        onboardingAudioGenerated: localStorage.getItem('onboardingAudioGenerated')
      });
      
      // Ensure the API URL is properly formatted
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      logger.debug(`Using base URL for API calls: ${baseUrl}`);
      
      // Log the exact endpoint being called
      const endpoint = `${baseUrl}/api/ai/audio/${id}/generate`;
      logger.debug(`Calling endpoint to generate audio: ${endpoint}`);
      
      // Verify the script exists and is script-only before attempting generation
      const scriptRecord = savedAudios.find(audio => audio._id === id);
      if (!scriptRecord) {
        logger.error(`PHASE 2: Script ID ${id} not found in savedAudios`);
        throw new Error(`Script ID ${id} not found`);
      }
      
      logger.debug(`PHASE 2: Script record state before generation:`, {
        id: scriptRecord._id,
        title: scriptRecord.title,
        isScriptOnly: scriptRecord.isScriptOnly,
        audioUrl: scriptRecord.audioUrl,
        hasScript: !!scriptRecord.script,
        scriptLength: scriptRecord.script ? scriptRecord.script.length : 0
      });
      
      if (!scriptRecord.isScriptOnly) {
        logger.warn(`PHASE 2: Script ${id} already has audio, skipping generation`);
        setMessage('This script already has audio generated.');
        setGenerating(false);
        return false;
      }
      
      logger.debug(`PHASE 2: Calling audio generation endpoint for script ID: ${id}`);
      
      // Call the individual audio generation endpoint
      const response = await axios.post(
        endpoint,
        {},
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 180000 // 3 minute timeout for individual audio generation
        }
      );
      
      logger.info(`PHASE 2: Audio generation successful for script ID ${id}:`, response.data);
      setMessage('Audio generated successfully!');
      
      // Refresh the saved audios list to show the new audio
      logger.debug(`PHASE 2: Refreshing saved audios after successful generation for ID: ${id}`);
      await fetchSavedAudios();
      
      setGenerating(false);
      return true; // Return true to indicate success
    } catch (error) {
      logger.error(`PHASE 2: API error during Mindset Audio generation for ID ${id}:`, error);
      
      // Check if it's a 404 error (script not found or already has audio)
      if (error.response && error.response.status === 404) {
        logger.warn(`PHASE 2: Script ID ${id} not found or already has audio, refreshing data`);
        // Still refresh saved audios to update UI
        await fetchSavedAudios();
      } else {
        logger.error(`PHASE 2: Detailed error for ID ${id}:`, {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
        setMessage(`Error generating audio: ${error.message || 'Unknown error'}`);
      }
      
      setGenerating(false);
      return false; // Return false to indicate failure
    }
  };

  const normalizeAudioUrl = (url) => {
    if (!url) return '';
    
    // If it's a pending URL, don't try to normalize it
    if (url === 'pending') {
      return '';
    }
    
    try {
      // Check if it's already a full URL
      new URL(url);
      return url; // It's already a valid URL
    } catch (e) {
      // It's not a valid URL, so it might be a relative path
      
      // Handle database-served audio URLs
      if (url.includes('/api/ai/audio/serve/')) {
        const baseUrl = process.env.NODE_ENV === 'production' 
          ? 'https://backend-rough-bird-115.fly.dev' 
          : API_URL;
        
        // If it already starts with the base URL, return as is
        if (url.startsWith('http')) {
          return url;
        }
        
        // Otherwise, prepend the base URL
        return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      }
      
      // Handle legacy file system URLs
      if (url.includes('/uploads/audio/')) {
        const baseUrl = process.env.NODE_ENV === 'production' 
          ? 'https://backend-rough-bird-115.fly.dev' 
          : API_URL;
        
        // If it already starts with the base URL, return as is
        if (url.startsWith('http')) {
          return url;
        }
        
        // Otherwise, prepend the base URL
        return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      }
      
      // If it's not a recognized format, return empty string
      logger.warn('Unrecognized audio URL format:', url);
      return '';
    }
  };

  const loadAndPlayAudio = async (url) => {
    try {
      setLoading(true);
      setError('');
      
      // Skip if URL is empty or pending
      if (!url || url === 'pending') {
        setError('Audio is not yet available. Please try again later.');
        setLoading(false);
        return;
      }
      
      // Normalize the URL to ensure it's a full URL
      const normalizedUrl = normalizeAudioUrl(url);
      
      if (!normalizedUrl) {
        setError('Invalid audio URL. Please try regenerating the audio.');
        setLoading(false);
        return;
      }
      
      logger.debug('Loading audio from URL:', normalizedUrl);
      
      // Create a new Audio element
      const audio = new Audio();
      
      // Set up event listeners
      audio.addEventListener('canplaythrough', () => {
        logger.debug('Audio loaded and can play through');
        audioRef.current = audio;
        setIsPlaying(true);
        audio.play().catch(error => {
          logger.error('Error playing audio:', error);
          setError('Error playing audio. Please try again.');
          setIsPlaying(false);
        });
        setLoading(false);
      });
      
      audio.addEventListener('error', (e) => {
        logger.error('Audio loading error:', e);
        setError('Failed to load audio. Please try again later.');
        setLoading(false);
        
        // If audio fails to load, try refreshing the saved audios to get updated URLs
        fetchSavedAudios();
      });
      
      // Add event listener to clean up the object URL when done
      const handleEnded = () => {
        setIsPlaying(false);
        // Don't revoke object URL for database-served audio
      };
      audio.addEventListener('ended', handleEnded);
      
      // Set the source and load the audio
      audio.src = normalizedUrl;
      audio.load();
      
    } catch (error) {
      logger.error('Error in loadAndPlayAudio:', error);
      setError('Error loading audio. Please try again.');
      setLoading(false);
      
      // If there's an error, refresh saved audios to get updated URLs
      fetchSavedAudios();
    }
  };

  const playAudio = () => {
    if (audioUrl) {
      loadAndPlayAudio(audioUrl);
    } else {
      setError('No audio URL available. Please generate audio first.');
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const downloadAudio = async () => {
    try {
      logger.debug('downloadAudio called with audioUrl:', audioUrl);
      
      if (!audioUrl) {
        logger.warn('downloadAudio: No audio URL available');
        setError('No audio available to download.');
        return;
      }
      
      if (audioUrl === 'processing') {
        logger.warn('downloadAudio: Audio is still being processed');
        setError('Audio is still being processed. Please wait a moment and try again.');
        return;
      }

      // Show loading message
      setMessage('Preparing download...');
      
      // Validate audio URL accessibility before download
      logger.debug('downloadAudio: Validating audio URL before download...');
      const validateResponse = await fetch(audioUrl, { method: 'HEAD' });
      if (!validateResponse.ok) {
        logger.error('downloadAudio: Audio URL validation failed:', validateResponse.status, validateResponse.statusText);
        throw new Error(`Audio file not accessible: ${validateResponse.status} ${validateResponse.statusText}`);
      }
      logger.debug('downloadAudio: Audio URL validation successful, proceeding with download');

      // Fetch the audio file
      logger.debug('downloadAudio: Fetching audio file...');
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
      }

      // Get the audio blob
      logger.debug('downloadAudio: Converting to blob...');
      const audioBlob = await response.blob();
      logger.debug('downloadAudio: Blob created, size:', audioBlob.size, 'bytes');

      // Create a download link
      const downloadUrl = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${inputText || 'mindset-audio'}-${new Date().toISOString().slice(0, 10)}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the URL object
      URL.revokeObjectURL(downloadUrl);
      
      // Show success message
      logger.info('downloadAudio: Download completed successfully');
      setMessage('Audio downloaded successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      logger.error('Error downloading audio:', error);
      
      // Provide specific error messages based on error type
      if (error.message.includes('not accessible')) {
        setError('Audio file is not ready yet. Please wait a moment and try again.');
        
        // Offer retry after a short delay
        setTimeout(() => {
          setError('');
          setMessage('You can try downloading the audio again now.');
          setTimeout(() => setMessage(''), 3000);
        }, 3000);
      } else {
        setError(`Failed to download audio: ${error.message}`);
      }
    }
  };

  const saveAudio = async () => {
    try {
      logger.debug('Saving audio to library');
      
      if (!inputText) {
        setError('No script to save. Please create a script first.');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Prompt user for a title
      const title = prompt('Enter a title for your visualization:', 
        selectedTemplate ? selectedTemplate.name : 'Custom Visualization');
      
      if (!title) {
        // User cancelled the prompt
        return;
      }
      
      if (audioUrl) {
        // If we have audio, convert the blob URL to a blob
        const response = await fetch(audioUrl);
        const audioBlob = await response.blob();
        
        // Create a FormData object to send the file
        const formData = new FormData();
        formData.append('audio', audioBlob, 'visualization.mp3');
        formData.append('title', title);
        formData.append('script', inputText);
        formData.append('settings', JSON.stringify(customSettings));
        
        // Send the request
        await axios.post(
          `${API_URL}/api/ai/save-audio`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // If we only have the script without audio, save just the script
        await axios.post(
          `${API_URL}/api/ai/save-audio`,
          {
            title: title,
            text: inputText,
            settings: customSettings
          },
          {
            headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
      logger.info('Visualization saved to library successfully');
      
      setMessage('Visualization saved to your library!');
      setTimeout(() => setMessage(''), 3000);
      
      // Refresh the saved audios list
      fetchSavedAudios();
      
    } catch (error) {
      logger.error('Error saving visualization to library:', error);
      setError('Failed to save visualization. Please try again.');
    }
  };

  const deleteAudio = async (audioId) => {
    try {
      logger.debug(`Deleting audio with ID: ${audioId}`);
      
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/ai/audios/${audioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      logger.info('Audio deleted successfully');
      
      // Update the UI
      setSavedAudios(prevAudios => prevAudios.filter(audio => audio._id !== audioId));
      
      setMessage('Audio deleted successfully!');
      setTimeout(() => setMessage(''), 3000);
      
    } catch (error) {
      logger.error('Error deleting audio');
      setError('Failed to delete audio. Please try again.');
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Handle recording functionality
  const requestRecordingPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setRecordingPermission(true);
      return stream;
    } catch (err) {
      logger.error('Error accessing microphone:', err);
      setError('Unable to access your microphone. Please check your browser permissions.');
      return null;
    }
  };

  const startRecording = async () => {
    try {
      logger.debug('Starting audio recording');
      
      // Reset recording state
      setRecordingTime(0);
      audioChunksRef.current = [];
      
      // Get the media stream
      const stream = recordingPermission ? 
        await navigator.mediaDevices.getUserMedia({ audio: true }) : 
        await requestRecordingPermission();
      
      if (!stream) return;
      
      // Create a new MediaRecorder instance
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          logger.debug('Recording data chunk received');
        }
      };
      
      mediaRecorder.onstop = () => {
        logger.debug('Recording stopped');
        
        // Combine the audio chunks into a single blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Set the recorded audio
        setRecordedAudio(audioUrl);
      };
      
      // Start the recording
      mediaRecorder.start();
      setIsRecording(true);
      
      // Start the timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);
      
    } catch (error) {
      logger.error('Error starting recording');
      setError('Failed to start recording. Please check your microphone permissions.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Clear the recording timer
      clearInterval(recordingTimerRef.current);
    }
  };
  
  const downloadRecording = () => {
    if (!recordedAudio) {
      setError('No recording available to download.');
      return;
    }
    
    // Create a temporary anchor element to trigger the download
    const a = document.createElement('a');
    a.href = recordedAudio;
    a.download = `mindset-script-recording-${new Date().getTime()}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Format seconds into MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Helper function to determine UI display state
  const getUIState = () => {
    // Check if we have a generated audio that's ready to play
    const hasGeneratedAudio = audioUrl && audioUrl !== 'pending' && !audioUrl.includes('pending');
    
    // Check if we have saved audios with generated audio (not script-only)
    const hasExistingAudio = savedAudios.some(audio => 
      audio.audioUrl && 
      !audio.isScriptOnly && 
      audio.audioUrl !== 'pending' && 
      !audio.audioUrl.includes('pending')
    );

    return {
      showScriptFields: !hasGeneratedAudio && !hasExistingAudio,
      showAudioPlayer: hasGeneratedAudio && !isPlayingFromLibrary,
      hasExistingAudio: hasExistingAudio
    };
  };

  const uiState = getUIState();

  const saveVisualization = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      
      // Validate required fields
      if (!inputText) {
        setError('Please generate or enter a script first.');
        setLoading(false);
        return;
      }
      
      const title = prompt('Enter a title for this visualization:', 
        selectedTemplate ? selectedTemplate.name : 'Custom Visualization');
      
      if (!title) {
        setLoading(false);
        return; // User cancelled the prompt
      }
      
      if (audioUrl) {
        // If we have audio, convert the blob URL to a blob
        const response = await fetch(audioUrl);
        const audioBlob = await response.blob();
        
        // Create a FormData object to send the file
        const formData = new FormData();
        formData.append('audio', audioBlob, 'visualization.mp3');
        formData.append('title', title);
        formData.append('script', inputText);
        formData.append('settings', JSON.stringify(customSettings));
        
        // Send the request
        await axios.post(
          `${API_URL}/api/ai/save-audio`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // If we only have the script without audio, save just the script
        await axios.post(
          `${API_URL}/api/ai/save-audio`,
          {
            title: title,
            text: inputText,
            settings: customSettings
          },
          {
            headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
      logger.info('Visualization saved to library successfully');
      
      setMessage('Visualization saved to your library!');
      setTimeout(() => setMessage(''), 3000);
      
      // Refresh the saved audios list
      fetchSavedAudios();
      
    } catch (error) {
      handleSaveError(error);
      setLoading(false);
    }
  };
  
  // Helper function to handle save errors
  const handleSaveError = (error) => {
    logger.error('Error saving visualization to library:', error);
    
    // Enhanced error handling with more specific messages
    if (error.response) {
      if (error.response.status === 400) {
        setError(`Invalid request: ${error.response.data.message || 'Missing required fields'}`);
      } else if (error.response.status === 401) {
        setError('Authentication expired. Please log in again.');
      } else {
        setError(`Server error: ${error.response.data.message || error.response.statusText}`);
      }
    } else if (error.request) {
      setError('No response from server. Please check your connection and try again.');
    } else {
      setError(`Failed to save visualization: ${error.message}`);
    }
  };

  const stopSpeechSynthesis = () => {
    if (window.speechSynthesis && speechSynthesisActive) {
      logger.debug('Stopping speech synthesis playback');
      window.speechSynthesis.cancel();
      setSpeechSynthesisActive(false);
    }
  };

  const playSpeechSynthesis = () => {
    if (speechSynthesisReady) {
      logger.debug('Starting speech synthesis playback');
      window.speechSynthesis.speak(speechSynthesisRef.current);
      setIsPlaying(true);
    }
  };

  return (
    <div className="w-full desktop-standard-container">
      <GuideModal guideKey="AI Mindset Minute" />
      <div className="grid grid-cols-1 gap-6">
        <div className="flex items-center mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="mr-4 p-2 rounded-full hover:bg-gray-200"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold">Mindset Audio</h1>
        </div>
        
        <p className="text-gray-600 text-lg mb-8">
          Create and practice mindset audio sessions to enhance your performance
        </p>

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <div className="flex items-center">
              <AlertCircle className="mr-2" />
              <p>{error}</p>
            </div>
          </div>
        )}
        
        {message && (
          <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-6 rounded">
            <div className="flex items-center">
              <CheckCircle className="mr-2" />
              <p>{message}</p>
            </div>
          </div>
        )}
      
        {/* Two-column layout */}
        <div className="flex flex-col md:flex-row md:space-x-6">
          {/* Main content area - Left column */}
          <div className="md:w-2/3">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 desktop-standard-card">
              {uiState.hasExistingAudio ? (
                // Audio exists - show only audio player interface
                <div>
                  <h2 className="text-2xl font-bold mb-6">Your Mindset Audio</h2>
                  <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        <button
                          onClick={isPlaying ? pauseAudio : playAudio}
                          className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full mr-3"
                          disabled={!audioUrl || audioUrl === "pending"}
                        >
                          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                        </button>
                        <div>
                          <h3 className="font-medium">
                            {selectedTemplate ? selectedTemplate.name : 'Custom Mindset'}
                          </h3>
                          <p className="text-sm text-gray-600">{customSettings.duration} minutes</p>
                        </div>
                      </div>
                      <button
                        onClick={downloadAudio}
                        className="text-red-500 hover:text-red-700 p-2"
                        title="Download Audio"
                      >
                        <Download size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // No audio - show script creation interface
                <div>
                  <h2 className="text-2xl font-bold mb-6">Create New Mindset Audio</h2>
                  
                  {uiState.showScriptFields && (
                    <>
                      <div className="mb-6">
                        <label className="block text-gray-700 text-lg font-medium mb-2">Title</label>
                        <input
                          type="text"
                          placeholder="Enter a title for your mindset audio session"
                          value={inputText}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-gray-700 text-lg font-medium">Script</label>
                          <button
                            onClick={generatePersonalizedScript}
                            disabled={isGeneratingPersonalizedScript}
                            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                          >
                            {isGeneratingPersonalizedScript ? (
                              <>
                                <Loader className="animate-spin mr-2" size={16} /> 
                                Generating...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="mr-2" size={16} />
                                Generate Script
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    
                      <textarea
                        className="w-full h-48 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter or generate a mindset audio script..."
                        value={userData.mindsetScript || ''}
                        onChange={(e) => setUserData({ ...userData, mindsetScript: e.target.value })}
                      ></textarea>
                      
                      <div className="text-right text-gray-500 text-sm mt-1 mb-6">
                        {(userData.mindsetScript || '').length}/4000 characters
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Audio Settings - show only when creating new audio */}
              {uiState.showScriptFields && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowCustomizer(!showCustomizer)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-blue-600"
                  >
                    <Settings size={16} />
                    <span>Audio Settings</span>
                    <span className="text-xs">{showCustomizer ? '▼' : '▶'}</span>
                  </button>
                  
                  {showCustomizer && (
                    <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-700 text-xs font-bold mb-1">Voice</label>
                          <select
                            value={customSettings.voice}
                            onChange={(e) => setCustomSettings({...customSettings, voice: e.target.value})}
                            className="w-full text-xs border rounded px-2 py-1"
                          >
                            <option value="calm">Calm</option>
                            <option value="energetic">Energetic</option>
                            <option value="soothing">Soothing</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-gray-700 text-xs font-bold mb-1">Background</label>
                          <select
                            value={customSettings.background}
                            onChange={(e) => setCustomSettings({...customSettings, background: e.target.value})}
                            className="w-full text-xs border rounded px-2 py-1"
                          >
                            <option value="none">None</option>
                            <option value="ambient">Ambient</option>
                            <option value="nature">Nature</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-gray-700 text-xs font-bold mb-1">Pace</label>
                          <select
                            value={customSettings.pace}
                            onChange={(e) => setCustomSettings({...customSettings, pace: e.target.value})}
                            className="w-full text-xs border rounded px-2 py-1"
                          >
                            <option value="slow">Slow</option>
                            <option value="medium">Medium</option>
                            <option value="fast">Fast</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-gray-700 text-xs font-bold mb-1">Duration (min)</label>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => setCustomSettings({...customSettings, duration: Math.max(5, customSettings.duration - 1)})}
                              className="bg-gray-200 hover:bg-gray-300 text-xs px-2 py-1 rounded"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="text-xs px-2">{customSettings.duration}</span>
                            <button
                              onClick={() => setCustomSettings({...customSettings, duration: Math.min(20, customSettings.duration + 1)})}
                              className="bg-gray-200 hover:bg-gray-300 text-xs px-2 py-1 rounded"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            
              <div className="mt-6 space-y-4">
                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={generateAudio}
                    disabled={loading || !userData.mindsetScript}
                    className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center ${loading || !userData.mindsetScript ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin mr-2" size={20} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Music className="mr-2" size={20} />
                        Generate Audio
                      </>
                    )}
                  </button>
                
                  {recordingPermission ? (
                    <button
                      onClick={isRecording ? stopRecording : startRecording}
                      className={`flex-1 ${isRecording ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center`}
                    >
                      {isRecording ? (
                        <>
                          <StopCircle className="mr-2" size={20} />
                          Stop ({formatTime(recordingTime)})
                        </>
                      ) : (
                        <>
                          <Mic className="mr-2" size={20} />
                          Record My Voice
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={requestRecordingPermission}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center"
                    >
                      <MicOff className="mr-2" size={20} />
                      Enable Recording
                    </button>
                  )}
                
                  <button
                    onClick={saveVisualization}
                    disabled={!audioUrl && !recordedAudio}
                    className={`flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center justify-center ${!audioUrl && !recordedAudio ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Save className="mr-2" size={20} />
                    Save to Library
                  </button>
                </div>
              </div>
            
              {recordedAudio && (
                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                  <h3 className="font-bold mb-2">Your Recording</h3>
                  <audio src={recordedAudio} controls className="w-full mb-3" />
                  <button
                    onClick={downloadRecording}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
                  >
                    <Download className="mr-2" size={20} />
                    Download Recording
                  </button>
                </div>
              )}
            
              {generating && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-6 mb-6 shadow-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="relative">
                        <Loader size={24} className="animate-spin text-green-600 mr-3" />
                        <div className="absolute inset-0 rounded-full border-2 border-green-200"></div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-green-900">Generating Your Mindset Audio</h3>
                        <p className="text-green-700 text-sm">This process typically takes 15-45 seconds...</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">🧠</div>
                    </div>
                  </div>
                  
                  <div className="bg-white bg-opacity-60 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between text-sm text-green-800 mb-2">
                      <span>Processing your mindset script with AI voice synthesis...</span>
                      <span className="font-medium">Please wait</span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-green-700">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                      <span>Converting text to high-quality audio...</span>
                    </div>
                    <span className="text-xs bg-green-100 px-2 py-1 rounded-full">
                      Stay on this page
                    </span>
                  </div>
                  
                  <div className="mt-4 p-3 bg-green-100 bg-opacity-50 rounded-lg">
                    <p className="text-xs text-green-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      <strong>Tip:</strong> Your personalized mindset audio will appear automatically when ready.
                    </p>
                  </div>
                </div>
              )}
            
              {speechSynthesisReady && (
                <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-6 rounded">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Volume2 size={24} className="mr-3 animate-pulse" />
                      <div>
                        <p className="font-medium">Browser Speech Synthesis Ready</p>
                        <p className="text-sm text-blue-100">Click "Play Speech" to start audio playback</p>
                      </div>
                    </div>
                    <button
                      onClick={playSpeechSynthesis}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
                    >
                      <Play size={20} className="mr-2" />
                      Play Speech
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Always render audio element for audioRef, but conditionally show UI */}
            <audio 
              ref={audioRef}
              {...(audioUrl && audioUrl !== 'pending' && { src: audioUrl })}
              onEnded={handleAudioEnded}
              onError={(e) => {
                console.error("Audio error:", e.target.error, "Audio URL:", audioUrl);
                setError("Failed to play audio. The file may be missing or in an unsupported format.");
                setIsPlaying(false);
              }}
              onCanPlay={() => {
                // Clear any previous errors when audio is ready to play
                if (error && error.includes("Failed to play audio")) {
                  setError("");
                }
              }}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              className="hidden"
            />
            
            {audioUrl && !isPlayingFromLibrary && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 desktop-standard-card">
                <h2 className="text-xl font-bold mb-4">Your Mindset Audio</h2>
                
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center">
                      <button
                        onClick={isPlaying ? pauseAudio : playAudio}
                        className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full mr-3"
                        disabled={!audioUrl || audioUrl === "pending"}
                      >
                        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                      </button>
                      <div>
                        <h3 className="font-medium">
                          {selectedTemplate ? selectedTemplate.name : 'Custom Mindset'}
                        </h3>
                        <p className="text-sm text-gray-600">{customSettings.duration} minutes</p>
                      </div>
                    </div>
                  </div>
                  
                  {(audioUrl && audioUrl !== "pending") ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">{formatTime(currentTime)}</span>
                        <span className="text-gray-600">{formatTime(duration)}</span>
                      </div>
                      <div className="w-full bg-gray-300 rounded-full h-1.5 mb-4">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{ width: `${(currentTime / duration) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                      <p className="text-yellow-700 flex items-center">
                        <AlertCircle size={18} className="mr-2" />
                        Audio is being prepared. You can still edit your script while waiting.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={saveVisualization}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
                  >
                    <Save className="mr-2" size={20} />
                    Save to My Library
                  </button>
                  <button
                    onClick={downloadAudio}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center justify-center"
                  >
                    <Download className="mr-2" size={20} />
                    Download Audio
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar - Right column */}
          <div className="md:w-1/3">
            {savedAudios.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 sticky top-4 desktop-standard-card">
                <h2 className="text-xl font-bold mb-4">My Mindset Library</h2>
                
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {savedAudios.map(audio => (
                    <div key={audio._id} className="bg-gray-100 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h3 className="font-medium">{audio.title}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(audio.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteAudio(audio._id)}
                          className="text-red-500 hover:text-red-700 p-2"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-2">
                        {audio.audioUrl && !audio.isScriptOnly && audio.audioUrl !== 'pending' ? (
                          // Audio exists and is ready - show Play button
                          <button
                            onClick={() => {
                              // Improved play functionality for saved audios
                              let audioUrlToPlay = audio.audioUrl;
                              
                              // Skip if URL is pending or invalid
                              if (!audioUrlToPlay || audioUrlToPlay === 'pending' || audioUrlToPlay.includes('pending')) {
                                setError('Audio is still being generated. Please wait or try refreshing.');
                                return;
                              }
                              
                              // Use normalizeAudioUrl function for consistent URL handling
                              const normalizedUrl = normalizeAudioUrl(audioUrlToPlay);
                              
                              if (!normalizedUrl) {
                                logger.error('Invalid audio URL format:', audioUrlToPlay);
                                setError('Invalid audio URL format. Please try regenerating the audio.');
                                return;
                              }
                              
                              logger.debug('Playing audio from saved list:', {
                                originalUrl: audioUrlToPlay,
                                normalizedUrl: normalizedUrl,
                                audioId: audio._id
                              });
                              
                              // Set the audio URL and prepare for playback
                              setAudioUrl(normalizedUrl);
                              setIsPlaying(false);
                              setIsPlayingFromLibrary(true);
                              
                              // Use a short timeout to ensure state is updated before playing
                              setTimeout(() => {
                                playAudio();
                              }, 100);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded flex items-center justify-center"
                          >
                            <Play size={16} className="mr-1" />
                            Play
                          </button>
                        ) : audio.isScriptOnly || audio.audioUrl === 'pending' || !audio.audioUrl ? (
                          // Script-only or pending - show Generate Audio button
                          <button
                            onClick={() => generateAudioForScript(audio._id)}
                            disabled={generating}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-3 py-1 rounded flex items-center justify-center"
                          >
                            {generating ? (
                              <>
                                <Loader size={16} className="mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Volume2 size={16} className="mr-1" />
                                Generate Audio
                              </>
                            )}
                          </button>
                        ) : (
                          // Invalid URL - show error message
                          <div className="text-red-500">
                            Invalid audio URL
                          </div>
                        )}
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {audio.settings?.duration || 5} min
                        </span>
                        {audio.isScriptOnly && (
                          <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            Script Only
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {savedAudios.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 sticky top-4 desktop-standard-card">
                <h2 className="text-xl font-bold mb-4">My Mindset Library</h2>
                <div className="bg-gray-50 p-6 rounded-lg text-center">
                  <Volume2 size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    You haven't created any mindset audio sessions yet. Create your first one!
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {speechSynthesisActive && (
        <div className="speech-synthesis-controls fixed bottom-4 right-4 z-50 bg-blue-600 text-white p-4 rounded-lg shadow-lg max-w-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Volume2 size={24} className="mr-3 animate-pulse" />
              <div>
                <p className="font-medium">Browser Speech Synthesis</p>
                <p className="text-sm text-blue-100">Playing your mindset script</p>
              </div>
            </div>
            <button 
              onClick={stopSpeechSynthesis}
              className="bg-white text-blue-600 hover:bg-blue-100 px-3 py-2 rounded-md font-medium flex items-center"
            >
              <StopCircle size={18} className="mr-1" />
              Stop
            </button>
          </div>
          <div className="mt-2 text-xs text-blue-200">
            <p>Note: Audio will stop if you navigate away from this page</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioVisualization;